import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAdminStore } from '../stores/adminStore';
import { Button } from '../components/common/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { Industry } from '../stores/industryStore';
import IndustryModalInfo from '../components/admin/modals/IndustryModalInfo';
import IndustryTable from '../components/admin/IndustryTable';
import AddIndustryModal from '../components/admin/modals/AddIndustryModal';

const IndustriesList: React.FC = () => {
  const { t } = useTranslation(['common', 'admin', 'jobs']);

  const location = useLocation();
  const navigate = useNavigate();

  const { industries, fetchIndustries, fetchMoreIndustries, hasMore } = useAdminStore();

  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const state = (location.state || {}) as { selected_id?: number };
    if (state?.selected_id) {
      const selectedId = Number(state.selected_id);
      const found = industries.find(i => i.id === selectedId);

      if (found) {
        setSelectedIndustry(found);
        setIsModalOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, industries, navigate]);

  useEffect(() => {
    setLoading(true);
    if (!industries.length) {
      fetchIndustries({ offsetCount: 0 })
        .catch(() => {
          toast.error(t('generic_error', { ns: 'common' }));
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [industries.length, fetchIndustries, t]);

  const handleRowClick = (industry: Industry) => {
    setSelectedIndustry(industry);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedIndustry(null);
    setOpenAddModal(true);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      await fetchMoreIndustries();
    } catch {
      toast.error(t('generic_error', { ns: 'common' }));
    } finally {
      setLoading(false);
    }
  };

  const industriesToShow = industries.filter(industry => !industry.deleted);

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('breadcrumbs.industries', { ns: 'common', defaultValue: 'Industries' })}
        </h1>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] transition-all">
        <div className="flex flex-wrap gap-3 items-center justify-end px-4 py-3 border-b border-gray-300 bg-gray-50">
          <Button
            type="button"
            variant="transparent-with-border"
            className="w-auto w-full text-left transition duration-300 ease-in-out hover:bg-gray-200 md:ml-auto md:px-8"
            onClick={handleAddClick}
          >
            {t('add_new_industry', { ns: 'common' })}
          </Button>
        </div>

        <div>
          <IndustryTable industries={industriesToShow} onRowClick={handleRowClick} />
        </div>

        {hasMore?.industries && (
          <div className="py-4 flex justify-center">
            <Button className="px-8" onClick={handleLoadMore} disabled={loading}>
              {loading
                ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
                : t('loadMore', { ns: 'common', defaultValue: 'Charger plus' })}
            </Button>
          </div>
        )}
        {isModalOpen && (
          <IndustryModalInfo industry={selectedIndustry} onClose={() => setIsModalOpen(false)} />
        )}

        {openAddModal && (
          <AddIndustryModal industry={null} onClose={() => setOpenAddModal(false)} />
        )}
      </div>
    </>
  );
};

export default IndustriesList;
